import { safeDeleteFromLocalStorage, safeGetFromLocalStorage, safeSetInLocalStorage } from '../../helpers/window';
import { amplitudeModule } from './AmplitudeModule';
import type { PrescriptionStartedPayload } from './types';
import type { PrescriptionCompletedPayload } from './types';
import type { PrescriptionCompletedProtocol } from './types';

const PRESCRIPTION_STARTED_KEY = (prescriptionId: number) => `amplitude_prescription_started_${prescriptionId}`;

/**
 * Persiste o payload do evento prescription_started no localStorage.
 * Usado para garantir que os dados do funil não sejam perdidos em caso de reload.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @param {PrescriptionStartedPayload} payload - Payload do evento a ser persistido.
 * @returns {void}
 */
export function savePrescriptionStartedPayload(prescriptionId: number, payload: PrescriptionStartedPayload): void {
  if (!amplitudeModule.isInitialized()) return;
  safeSetInLocalStorage(PRESCRIPTION_STARTED_KEY(prescriptionId), JSON.stringify(payload));
}

/**
 * Recupera o payload do evento prescription_started do localStorage.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {PrescriptionStartedPayload | null} O payload persistido ou `null` quando ausente/inválido.
 */
export function getPrescriptionStartedPayload(prescriptionId: number): PrescriptionStartedPayload | null {
  if (!amplitudeModule.isInitialized()) return null;
  const raw = safeGetFromLocalStorage(PRESCRIPTION_STARTED_KEY(prescriptionId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PrescriptionStartedPayload;
  } catch {
    return null;
  }
}

/**
 * Remove o payload do evento prescription_started do localStorage.
 * Deve ser chamado ao cancelar ou finalizar a prescrição.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {void}
 */
export function clearPrescriptionStartedPayload(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(PRESCRIPTION_STARTED_KEY(prescriptionId));
}

const PRESCRIPTION_COMPLETED_KEY = (prescriptionId: number) => `amplitude_prescription_completed_${prescriptionId}`;

/**
 * Persiste o payload do evento prescription_completed no localStorage.
 * Usado para acumular progressivamente os dados ao longo da jornada de prescrição.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @param {PrescriptionCompletedPayload} payload - Payload do evento a ser persistido.
 * @returns {void}
 */
export function savePrescriptionCompletedPayload(prescriptionId: number, payload: PrescriptionCompletedPayload): void {
  if (!amplitudeModule.isInitialized()) return;
  safeSetInLocalStorage(PRESCRIPTION_COMPLETED_KEY(prescriptionId), JSON.stringify(payload));
}

/**
 * Recupera o payload do evento prescription_completed do localStorage.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {PrescriptionCompletedPayload | null} O payload persistido ou `null` quando ausente/inválido.
 */
export function getPrescriptionCompletedPayload(prescriptionId: number): PrescriptionCompletedPayload | null {
  if (!amplitudeModule.isInitialized()) return null;
  const raw = safeGetFromLocalStorage(PRESCRIPTION_COMPLETED_KEY(prescriptionId));
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PrescriptionCompletedPayload;
  } catch {
    return null;
  }
}

/**
 * Remove o payload do evento prescription_completed do localStorage.
 * Deve ser chamado ao cancelar ou finalizar a prescrição.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {void}
 */
export function clearPrescriptionCompletedPayload(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(PRESCRIPTION_COMPLETED_KEY(prescriptionId));
}

/**
 * Dados de origem de concomitância de um medicamento.
 *
 * @property {boolean} hasConcomitance - Indica se o medicamento foi adicionado via concomitância.
 * @property {number} originId - ID do medicamento de origem que gerou a concomitância.
 * @property {string} originName - Nome do medicamento de origem que gerou a concomitância.
 */
export interface ConcomitanceOriginData {
  hasConcomitance: boolean;
  originId: number;
  originName: string;
}

const CONCOMITANCE_MAP_KEY = (prescriptionId: number) => `amplitude_concomitance_map_${prescriptionId}`;

/**
 * Salva os dados de origem de concomitância para um medicamento adicionado via concomitância.
 * Chave: idMedicamento do medicamento concomitante adicionado.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @param {number} concomitantMedicineId - ID do medicamento concomitante adicionado (chave do mapa).
 * @param {ConcomitanceOriginData} data - Dados de origem da concomitância.
 * @returns {void}
 */
export function saveConcomitanceOrigin(
  prescriptionId: number,
  concomitantMedicineId: number,
  data: ConcomitanceOriginData
): void {
  if (!amplitudeModule.isInitialized()) return;
  const raw = safeGetFromLocalStorage(CONCOMITANCE_MAP_KEY(prescriptionId));
  const map: Record<number, ConcomitanceOriginData> = raw ? JSON.parse(raw) : {};
  map[concomitantMedicineId] = data;
  safeSetInLocalStorage(CONCOMITANCE_MAP_KEY(prescriptionId), JSON.stringify(map));
}

/**
 * Recupera o mapa completo de concomitâncias para uma prescrição.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {Record<number, ConcomitanceOriginData>} Mapa de idMedicamento para dados de concomitância (vazio quando ausente).
 */
export function getConcomitanceMap(
  prescriptionId: number
): Record<number, ConcomitanceOriginData> {
  if (!amplitudeModule.isInitialized()) return {};
  const raw = safeGetFromLocalStorage(CONCOMITANCE_MAP_KEY(prescriptionId));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<number, ConcomitanceOriginData>;
  } catch {
    return {};
  }
}

/**
 * Remove o mapa de concomitâncias do localStorage.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {void}
 */
export function clearConcomitanceMap(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(CONCOMITANCE_MAP_KEY(prescriptionId));
}

/**
 * Dados de posologia sugerida de um medicamento.
 *
 * @property {boolean} isSuggestedPosology - Indica se a posologia foi selecionada a partir de uma sugestão.
 * @property {number | null} suggestedPosologyId - ID da posologia sugerida ou `null` quando não aplicável.
 */
export interface SuggestedPosologyData {
  isSuggestedPosology: boolean;
  suggestedPosologyId: number | null;
}

const SUGGESTED_POSOLOGY_MAP_KEY = (prescriptionId: number) => `amplitude_suggested_posology_map_${prescriptionId}`;

/**
 * Salva os dados de posologia sugerida para um medicamento.
 * Chave: idMedicamento.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @param {number} medicineId - ID do medicamento (chave do mapa).
 * @param {SuggestedPosologyData} data - Dados da posologia sugerida.
 * @returns {void}
 */
export function saveSuggestedPosology(
  prescriptionId: number,
  medicineId: number,
  data: SuggestedPosologyData
): void {
  if (!amplitudeModule.isInitialized()) return;
  const raw = safeGetFromLocalStorage(SUGGESTED_POSOLOGY_MAP_KEY(prescriptionId));
  const map: Record<number, SuggestedPosologyData> = raw ? JSON.parse(raw) : {};
  map[medicineId] = data;
  safeSetInLocalStorage(SUGGESTED_POSOLOGY_MAP_KEY(prescriptionId), JSON.stringify(map));
}

/**
 * Recupera o mapa de posologias sugeridas para uma prescrição.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {Record<number, SuggestedPosologyData>} Mapa de idMedicamento para dados de posologia sugerida (vazio quando ausente).
 */
export function getSuggestedPosologyMap(
  prescriptionId: number
): Record<number, SuggestedPosologyData> {
  if (!amplitudeModule.isInitialized()) return {};
  const raw = safeGetFromLocalStorage(SUGGESTED_POSOLOGY_MAP_KEY(prescriptionId));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<number, SuggestedPosologyData>;
  } catch {
    return {};
  }
}

/**
 * Remove o mapa de posologias sugeridas do localStorage.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {void}
 */
export function clearSuggestedPosologyMap(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(SUGGESTED_POSOLOGY_MAP_KEY(prescriptionId));
}

const STRUCTURED_POSOLOGY_MAP_KEY = (prescriptionId: number) => `amplitude_structured_posology_map_${prescriptionId}`;

/**
 * Salva a flag de posologia estruturada de um medicamento.
 * Necessário porque a flag `isStructuredPosology` é apenas client-side e se perde
 * quando a prescrição é re-buscada da API no momento da emissão do evento.
 * Chave: idMedicamento.
 */
export function saveStructuredPosology(
  prescriptionId: number,
  medicineId: number,
  isStructuredPosology: boolean
): void {
  if (!amplitudeModule.isInitialized()) return;
  const raw = safeGetFromLocalStorage(STRUCTURED_POSOLOGY_MAP_KEY(prescriptionId));
  const map: Record<number, boolean> = raw ? JSON.parse(raw) : {};
  map[medicineId] = isStructuredPosology;
  safeSetInLocalStorage(STRUCTURED_POSOLOGY_MAP_KEY(prescriptionId), JSON.stringify(map));
}

/**
 * Recupera o mapa de posologias estruturadas para uma prescrição.
 */
export function getStructuredPosologyMap(prescriptionId: number): Record<number, boolean> {
  if (!amplitudeModule.isInitialized()) return {};
  const raw = safeGetFromLocalStorage(STRUCTURED_POSOLOGY_MAP_KEY(prescriptionId));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<number, boolean>;
  } catch {
    return {};
  }
}

/**
 * Remove o mapa de posologias estruturadas do localStorage.
 */
export function clearStructuredPosologyMap(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(STRUCTURED_POSOLOGY_MAP_KEY(prescriptionId));
}

const PROTOCOL_MAP_KEY = (prescriptionId: number) => `amplitude_protocol_map_${prescriptionId}`;

/**
 * Salva os dados de um modelo de prescrição (protocolo) utilizado na composição da prescrição.
 * Chave: id do modelo.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @param {PrescriptionCompletedProtocol} protocol - Dados do modelo (protocolo); `protocol.id` é usado como chave do mapa.
 * @returns {void}
 */
export function saveProtocolData(
  prescriptionId: number,
  protocol: PrescriptionCompletedProtocol
): void {
  if (!amplitudeModule.isInitialized()) return;
  const raw = safeGetFromLocalStorage(PROTOCOL_MAP_KEY(prescriptionId));
  const map: Record<string, PrescriptionCompletedProtocol> = raw ? JSON.parse(raw) : {};
  map[protocol.id] = protocol;
  safeSetInLocalStorage(PROTOCOL_MAP_KEY(prescriptionId), JSON.stringify(map));
}

/**
 * Recupera o mapa de protocolos (modelos de prescrição) para uma prescrição.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {Record<number, PrescriptionCompletedProtocol>} Mapa de id do modelo para dados do protocolo (vazio quando ausente).
 */
export function getProtocolMap(
  prescriptionId: number
): Record<string, PrescriptionCompletedProtocol> {
  if (!amplitudeModule.isInitialized()) return {};
  const raw = safeGetFromLocalStorage(PROTOCOL_MAP_KEY(prescriptionId));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, PrescriptionCompletedProtocol>;
  } catch {
    return {};
  }
}

/**
 * Remove o mapa de protocolos do localStorage.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {void}
 */
export function clearProtocolMap(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(PROTOCOL_MAP_KEY(prescriptionId));
}

/**
 * Dados do laboratório escolhido pelo médico (feature de genéricos) para um medicamento.
 *
 * @property {string} laboratory - Nome do laboratório escolhido.
 * @property {boolean} isHighlighted - Indica se o laboratório escolhido é o patrocinado/destacado.
 */
export interface SelectedLaboratoryData {
  laboratory: string;
  isHighlighted: boolean;
}

const SELECTED_LABORATORY_MAP_KEY = (prescriptionId: number) => `amplitude_selected_laboratory_map_${prescriptionId}`;

/**
 * Salva o laboratório escolhido pelo médico (feature de genéricos) para um medicamento.
 * Chave: idMedicamento.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @param {number} medicineId - ID do medicamento (chave do mapa).
 * @param {SelectedLaboratoryData} data - Dados do laboratório escolhido.
 * @returns {void}
 */
export function saveSelectedLaboratory(
  prescriptionId: number,
  medicineId: number,
  data: SelectedLaboratoryData
): void {
  if (!amplitudeModule.isInitialized()) return;
  const raw = safeGetFromLocalStorage(SELECTED_LABORATORY_MAP_KEY(prescriptionId));
  const map: Record<number, SelectedLaboratoryData> = raw ? JSON.parse(raw) : {};
  map[medicineId] = data;
  safeSetInLocalStorage(SELECTED_LABORATORY_MAP_KEY(prescriptionId), JSON.stringify(map));
}

/**
 * Recupera o mapa de laboratórios escolhidos para uma prescrição.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {Record<number, SelectedLaboratoryData>} Mapa de idMedicamento para dados do laboratório escolhido (vazio quando ausente).
 */
export function getSelectedLaboratoryMap(
  prescriptionId: number
): Record<number, SelectedLaboratoryData> {
  if (!amplitudeModule.isInitialized()) return {};
  const raw = safeGetFromLocalStorage(SELECTED_LABORATORY_MAP_KEY(prescriptionId));
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<number, SelectedLaboratoryData>;
  } catch {
    return {};
  }
}

/**
 * Remove o mapa de laboratórios escolhidos do localStorage.
 *
 * @param {number} prescriptionId - ID da prescrição usada como chave de armazenamento.
 * @returns {void}
 */
export function clearSelectedLaboratoryMap(prescriptionId: number): void {
  if (!amplitudeModule.isInitialized()) return;
  safeDeleteFromLocalStorage(SELECTED_LABORATORY_MAP_KEY(prescriptionId));
}
